import { useApolloClient, useQuery } from '@apollo/client';
import classNames from 'classnames';
import { atom, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import _ from 'lodash';
import Quill, { Delta } from 'quill/core';
import type { Range } from 'quill/core/selection';
import React, {
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import BorderedIcon from '../components/BorderedIcon';
import DeltaPreview from '../components/DeltaPreview';
import type {
  DropdownMenuItemType,
  DropdownMenuSection,
} from '../components/DropdownMenu';
import DropdownMenu from '../components/DropdownMenu';
import SVGIcon from '../components/SVGIcon';
import SelectEntityOption from '../components/SelectEntityOption';
import { BEACON_SEARCH_INPUT } from '../constants/onboarding';
import { usePrefixListener } from '../editor/PrefixListener';
import { useQuill, withQuillContextProvider } from '../editor/QuillContext';
import type QuillEditor from '../editor/QuillEditor';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import GroupIcon from '../groups/GroupIcon';
import aiIcon from '../icons/non-prefilled/16x16/ai.svg';
import draftsIcon from '../icons/non-prefilled/16x16/drafts.svg';
import filterIcon from '../icons/non-prefilled/16x16/filter.svg';
import homeIcon from '../icons/non-prefilled/16x16/home.svg';
import insightsIcon from '../icons/non-prefilled/16x16/insights.svg';
import searchIcon from '../icons/non-prefilled/16x16/search.svg';
import settingsIcon from '../icons/non-prefilled/16x16/settings.svg';
import subtopicsIcon from '../icons/non-prefilled/16x16/subtopics.svg';
import teamIcon from '../icons/non-prefilled/16x16/team.svg';
import unorganizedIcon from '../icons/prefilled/16x16/unorganized.svg';
import TutorialBeacon from '../onboarding/tutorial/TutorialBeacon';
import PostSelectOption from '../post/PostSelectOption';
import populatePostsWithFullTopics from '../post/populatePostsWithFullTopics';
import useAllSlimPosts from '../post/useAllSlimPosts';
import type { PostEntity } from '../search/entitySearch/localEntitySearch';
import { postToEntity } from '../search/entitySearch/localEntitySearch';
import type {
  UseEntitySearchConfig,
  UseEntitySearchResult,
} from '../search/entitySearch/useEntitySearch';
import useEntitySearch from '../search/entitySearch/useEntitySearch';
import useIsAiAskEnabled from '../search/useIsAiAskEnabled';
import TemplateIcon from '../templates/TemplateIcon';
import TopicSelectOption from '../topic/TopicSelectOption';
import { useAllTopicMap } from '../topic/useAllTopics';
import { UIActions } from '../ui/uiSlice';
import UserSelectOption from '../user/UserSelectOption';
import { useAllUsersMap } from '../user/useAllUsers';
import nonEmpty from '../utils/nonEmpty';
import useSlug from '../utils/router/useSlug';
import type { V3To } from '../utils/router/v3Bridge';
import { useRouter } from '../utils/router/v3Bridge';
import { getAiAskLocation, isAiQuestion } from '../utils/search/aiAsk';
import searchHighlight from '../utils/search/searchHighlight';
import { deltaToText } from '../utils/text/deltaText';
import postPath from '../utils/url/postPath';
import topicPath from '../utils/url/topicPath';
import userPath from '../utils/url/userPath';
import FacetDropdown from './FacetDropdown';
import FacetEditProvider from './FacetEditContext';
import FacetedSearchInput from './FacetedSearchInput';
import styles from './SearchBar.module.scss';
import SearchDatePicker from './SearchDatePicker';
import SearchLinkSelectOption from './SearchLinkSelectOption';
import { SearchObjectsProvider } from './SearchObjects';
import type { DateFacet, EntityFacet, Facet } from './quillFacets';
import {
  createSearchFacetOp,
  deserializeQuery,
  FACETS,
  getSeparator,
  isSearchFacetOp,
  serializeQuery,
  TOPIC_FACET,
  USER_FACET,
} from './quillFacets';

const searchBarQuillAtom = atom<QuillEditor | null>(null);

export function appendFacet(quill: Quill, facet: Facet) {
  const content = quill.getContents();
  const lastOp = _.last(content.ops);

  const lastOpText = typeof lastOp?.insert === 'string' ? lastOp.insert : '';
  const prefixWithSeparator = `${facet.prefix}${getSeparator(facet)}`;

  const matches = lastOpText.matchAll(new RegExp(prefixWithSeparator, 'g'));
  const match = _.last([...matches]);

  if (match) {
    const offset = lastOpText.length - (match.index ?? 0);
    quill.focus();
    quill.setSelection({
      index: quill.getLength() - offset + prefixWithSeparator.length,
      length: offset - prefixWithSeparator.length,
    });
    return;
  }

  const contentText = deltaToText(content.ops);
  const value =
    contentText && contentText.trimEnd().length > 0
      ? ` ${prefixWithSeparator}`
      : prefixWithSeparator;

  quill.insertText(quill.getLength() - 1, value, Quill.sources.USER);
  quill.focus();
  quill.setSelection({
    index: quill.getLength(),
    length: 0,
  });
}

export const useAppendSearchBarFacet = () =>
  useAtomCallback(
    useCallback((get, _set, facet: Facet) => {
      const quill = get(searchBarQuillAtom);
      if (!quill) {
        return;
      }
      appendFacet(quill, facet);
    }, []),
  );

const MAX_RESULT_COUNT = 6;
const HALF_RESULT_COUNT = 3;
const ENTITY_SEARCH_OPTIONS = { limit: MAX_RESULT_COUNT };

const RECENTLY_VIEWED_POST_FRAGMENT = gql(`
  # eslint-disable-next-line slab-graphql/no-unused-fields
  fragment SearchBarRecentlyViewedPost on Post {
    id
    title
    publishedAt
    archivedAt
    views
    series {
      id
    }
    ...populatePostsWithFullTopics_posts
    ...postPath_post
    ...PostSelectOption_post
  }

`);

type ViewedPostFragment = DocumentType<typeof RECENTLY_VIEWED_POST_FRAGMENT>;

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
const VIEWED_POSTS_QUERY = gql(`
  query searchBarViewedPosts {
    session: currentSession {
      user {
        id
        viewedPosts(first: 25) {
          edges {
            node {
              ...SearchBarRecentlyViewedPost
            }
          }
        }
      }
    }
  }
`);

type NavOption = [string, string | React.ReactNode, string, string];

const NAV_OPTIONS: NavOption[] = [
  [
    'Content Map',
    <SVGIcon className={classNames(styles.navIcon, styles.contentMapIcon)}>
      {subtopicsIcon}
    </SVGIcon>,
    '/topics',
    'All topics and published posts',
  ],
  [
    'Drafts',
    <BorderedIcon>
      <SVGIcon className={styles.draftIcon}>{draftsIcon}</SVGIcon>
    </BorderedIcon>,
    '/drafts',
    'Unpublished posts',
  ],
  [
    'Home',
    <SVGIcon className={classNames(styles.navIcon, styles.homeIcon)}>
      {homeIcon}
    </SVGIcon>,
    '/',
    'Landing for team knowledge',
  ],
  [
    'Personal settings',
    <BorderedIcon>{settingsIcon}</BorderedIcon>,
    '/settings',
    'User settings for you',
  ],
  ['Groups', <GroupIcon />, '/groups', 'Organize and manage users'],
  [
    'Templates',
    <TemplateIcon />,
    '/templates',
    'Starting content for new posts',
  ],
  [
    'Unorganized',
    <SVGIcon className={classNames(styles.navIcon, styles.unorganizedIcon)}>
      {unorganizedIcon}
    </SVGIcon>,
    '/unorganized',
    'Published posts without a topic',
  ],
];

const ADMIN_NAV_OPTIONS: NavOption[] = [
  ...NAV_OPTIONS,
  [
    'Insights',
    <SVGIcon className={classNames(styles.navIcon, styles.insightsIcon)}>
      {insightsIcon}
    </SVGIcon>,
    '/admin/insights',
    'Usage and activity metrics',
  ],
  [
    'Team settings',
    <BorderedIcon>{settingsIcon}</BorderedIcon>,
    '/admin/appearance',
    'Admin settings for your team',
  ],
  [
    'Teammates',
    <BorderedIcon>{teamIcon}</BorderedIcon>,
    '/admin/teammates',
    'User account management',
  ],
];

const useRecentlyViewedSection = (options: {
  skip?: boolean;
  limit: number;
}) => {
  const dispatch = useAppDispatch();
  const client = useApolloClient();

  const recentlyViewedPostIds = useAppSelector(
    (state) => state.ui.recentlyViewedPostIds,
  );

  const recentlyViewedPosts = useMemo(
    () =>
      _.compact(
        recentlyViewedPostIds.map((id) =>
          client.readFragment({
            id: `Post:${id}`,
            fragment: RECENTLY_VIEWED_POST_FRAGMENT,
            fragmentName: 'SearchBarRecentlyViewedPost',
          }),
        ),
      ),
    [client, recentlyViewedPostIds],
  );

  const { data: viewedPostsData } = useQuery(VIEWED_POSTS_QUERY, {
    skip: options.skip || recentlyViewedPosts.length > 0,
  });

  const topicMap = useAllTopicMap();

  const posts = useMemo<ViewedPostFragment[]>(
    () =>
      populatePostsWithFullTopics(
        _.uniqBy(
          [
            ...recentlyViewedPosts,
            ...(viewedPostsData?.session?.user?.viewedPosts?.edges
              ?.map((edge) => edge?.node)
              .filter(nonEmpty) ?? []),
          ],
          'id',
        ),
        topicMap,
      ),
    [recentlyViewedPosts, topicMap, viewedPostsData],
  );

  useEffect(() => {
    if (recentlyViewedPosts.length === 0 && posts.length > 0) {
      dispatch(
        UIActions.addToRecentlyViewedPostIds(posts.map((post) => post.id)),
      );
    }
  }, [recentlyViewedPosts.length, posts, dispatch]);

  if (options.skip) {
    return null;
  }
  return {
    label: 'Recently Viewed',
    items: posts.slice(0, options.limit).map(
      (post): DropdownMenuItemType<PostEntity<ViewedPostFragment>> => ({
        id: post.id,
        hotkeyAction: 'jump',
        data: postToEntity(post),
        link: postPath(post),
      }),
    ),
  };
};

const USE_SAVE_POST_TO_RECENTLY_VIEWED_POST_FRAGMENT = gql(`
  fragment useSavePostToRecentlyViewed_post on Post {
    id
    ...SearchBarRecentlyViewedPost
  }
`);

export const useSavePostToRecentlyViewed = (
  post?: DocumentType<typeof USE_SAVE_POST_TO_RECENTLY_VIEWED_POST_FRAGMENT>,
) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (post?.title) {
      dispatch(UIActions.addToRecentlyViewedPostIds([post.id]));
    }
  }, [dispatch, post]);
};

type SearchOptionData = {
  type: 'searchOption';
  icon: React.ReactNode;
  label: React.ReactNode;
  query?: Delta;
  secondaryTop?: string;
  secondary?: React.ReactNode;
};

type SearchLinkData = {
  type: 'searchLink';
  label: React.ReactNode;
};

const useSuggestedFacet = ({
  skip,
}: {
  skip: boolean;
}): DropdownMenuSection<SearchOptionData> | null => {
  const [userId] = useSlug('userSlug');
  const [topicId] = useSlug('topicSlug');

  const allTopicMap = useAllTopicMap();
  const allUsersMap = useAllUsersMap({ skip: skip || !userId });

  const topic = allTopicMap[topicId ?? ''];
  const user = allUsersMap[userId ?? ''];

  if (!skip && (topic || user)) {
    const name = topic?.name ?? user?.name;

    const query = (
      topic
        ? new Delta([createSearchFacetOp(TOPIC_FACET, topicId)])
        : new Delta([createSearchFacetOp(USER_FACET, userId)])
    ).insert(' \n');

    return {
      items: [
        {
          id: 'suggested',
          data: {
            type: 'searchOption',
            icon: <BorderedIcon>{filterIcon}</BorderedIcon>,
            label: (
              <div className={styles.facet}>
                {topic ? 'in' : 'by'}: {name}
              </div>
            ),
            query,
          },
        },
      ],
    };
  }

  return null;
};

const useNavigateSearchItems = ({ query }: { query: string }) => {
  const isAdmin = useAppSelector(
    (state) => state.session.user.type === 'ADMIN',
  );

  const navOptions = isAdmin ? ADMIN_NAV_OPTIONS : NAV_OPTIONS;
  const navResults = navOptions
    .filter(
      ([name, , , secondary]) =>
        name?.toLowerCase().includes(query.toLowerCase()) ||
        secondary?.toLowerCase().includes(query.toLowerCase()),
    )
    .map(
      ([
        name,
        icon,
        link,
        secondary,
      ]): DropdownMenuItemType<SearchOptionData> => ({
        id: name,
        hotkeyAction: 'jump',
        data: {
          type: 'searchOption',
          icon,
          label: searchHighlight(name, query),
          secondary: searchHighlight(secondary, query),
        },
        link,
      }),
    );

  return navResults;
};

const useLocalSearchSection = ({
  query,
  config,
  limit,
}: {
  config: UseEntitySearchConfig;
  query: string;
  limit: number;
}): DropdownMenuSection<UseEntitySearchResult> | null => {
  const [debouncedQuery] = useDebounce(query, 100);
  const entities = useEntitySearch(
    debouncedQuery,
    config,
    ENTITY_SEARCH_OPTIONS,
  )
    .slice(0, limit)
    .map((entity): DropdownMenuItemType<UseEntitySearchResult> => {
      let link: string | undefined;
      switch (entity.type) {
        case 'post':
          link = postPath(entity.post);
          break;
        case 'topic':
          link = topicPath(entity.topic);
          break;
        case 'user':
          link = userPath(entity.user);
          break;
        default:
          break;
      }

      return {
        id: entity.id,
        hotkeyAction: 'jump',
        data: entity,
        link,
      };
    });

  if (entities.length > 0) {
    return {
      label: 'Jump to destination',
      items: entities,
    };
  }
  return null;
};

type Dropdown =
  | { type: 'suggestions' }
  | { type: 'entity'; range: Range; facet: EntityFacet }
  | { type: 'date'; range: Range; facet: DateFacet };

const useSearchSections = ({
  query,
  queryString,
  quickJump,
  searchLocation,
  aiAskLocation,
  dropdown,
}: {
  query: Delta;
  queryString: string;
  quickJump: boolean;
  searchLocation?: V3To;
  aiAskLocation?: V3To;
  dropdown: Dropdown | null;
}) => {
  const hasEmptyQuerySections =
    dropdown?.type === 'suggestions' && !queryString;
  const hasFilledQuerySections =
    dropdown?.type === 'suggestions' && !!queryString;

  const suggestedFacet = useSuggestedFacet({ skip: !hasEmptyQuerySections });
  const navigateSearchItems = useNavigateSearchItems({ query: queryString });

  // Slice the first section based on the navigate results count
  const limit =
    MAX_RESULT_COUNT -
    Math.min(navigateSearchItems.length ?? 0, HALF_RESULT_COUNT);

  const searchConfig = useMemo<UseEntitySearchConfig>(
    () =>
      hasFilledQuerySections
        ? {
            topic: {},
            post: { filter: (post) => !post.archivedAt },
            user: {},
          }
        : {},
    [hasFilledQuerySections],
  );

  // Preload data so when user start typing, local search doesn't need
  // to load posts through network first.
  useAllSlimPosts();

  const queryWithoutFacets = deltaToText(query.ops) ?? '';
  const localResultsSection = useLocalSearchSection({
    config: searchConfig,
    query: queryWithoutFacets,
    limit,
  });

  const recentlyViewedSection = useRecentlyViewedSection({
    skip: !hasEmptyQuerySections,
    limit,
  });

  const isAiAskEnabled = useIsAiAskEnabled();
  const isShowingAiAsk = isAiAskEnabled && isAiQuestion(queryString);

  const searchSection: DropdownMenuSection<SearchOptionData> | null =
    hasEmptyQuerySections
      ? null
      : {
          items: _.compact([
            {
              id: 'search',
              hotkeyAction: 'search',
              data: {
                type: 'searchOption',
                icon: <BorderedIcon>{searchIcon}</BorderedIcon>,
                label: <DeltaPreview content={query} typography="searchBar" />,
                secondaryTop: isShowingAiAsk ? 'Search' : undefined,
              },
              link: searchLocation,
            } satisfies DropdownMenuItemType<SearchOptionData>,
            isShowingAiAsk
              ? ({
                  id: 'aiAsk',
                  hotkeyAction: 'ask',
                  data: {
                    type: 'searchOption',
                    icon: <SVGIcon className={styles.aiIcon}>{aiIcon}</SVGIcon>,
                    label: (
                      <DeltaPreview content={query} typography="searchBar" />
                    ),
                    secondaryTop: 'AI Ask',
                  },
                  link: aiAskLocation,
                } satisfies DropdownMenuItemType<SearchOptionData>)
              : null,
          ]),
        };

  const searchListSection: DropdownMenuSection<SearchLinkData> | null =
    hasEmptyQuerySections
      ? null
      : {
          items: [
            {
              id: 'searchLink',
              data: { type: 'searchLink', label: 'Show all results' },
              link: searchLocation,
            },
          ],
        };

  const resultsSection = hasEmptyQuerySections
    ? recentlyViewedSection
    : localResultsSection;

  const resultsSectionLength = resultsSection?.items.length ?? 0;

  const navigateSearchSection =
    navigateSearchItems.length > 0
      ? {
          label: 'Navigate',
          items: navigateSearchItems.slice(
            0,
            MAX_RESULT_COUNT - resultsSectionLength,
          ),
        }
      : null;

  const sections: DropdownMenuSection<
    | PostEntity<ViewedPostFragment>
    | UseEntitySearchResult
    | SearchLinkData
    | SearchOptionData
  >[] = _.compact([
    suggestedFacet,
    ...(quickJump
      ? [resultsSection, searchSection]
      : [searchSection, resultsSection]),
    navigateSearchSection,
    searchListSection,
  ]);

  const firstSection = sections[0];
  if (sections.length > 1 && firstSection && !firstSection.label) {
    return [
      { ...firstSection, className: styles.sectionSeparator },
      ...sections.slice(1),
    ];
  }

  return sections;
};

const USE_PREFIX_LISTENER_OPTIONS: string[] = FACETS.flatMap((facet) => {
  const separator = getSeparator(facet);
  const prefix = `${facet.prefix}${separator}`;
  const alternativePrefixes: string[] = (facet.alternativePrefixes ?? []).map(
    (p) => `${p}${separator}`,
  );
  return [prefix, ...alternativePrefixes];
});

function usePrefixTriggeredFacet(quill: Quill | null) {
  const { prefix, text, range } = usePrefixListener(
    quill,
    USE_PREFIX_LISTENER_OPTIONS,
  );

  const [selection] = quill?.selection.getRange() ?? [];
  const isAfterPrefix =
    selection && range && selection.index >= range.index + range.length;

  if (!isAfterPrefix) {
    return {
      triggeredFacet: null,
      text: '',
      range: null,
    };
  }

  const triggeredFacet = FACETS.find(
    (f) =>
      (prefix?.startsWith(f.prefix) ||
        f.alternativePrefixes?.find((p) => prefix?.startsWith(p))) &&
      prefix?.endsWith(getSeparator(f)),
  );

  return { triggeredFacet, text: text ?? '', range };
}

const SearchBarContext = React.createContext<{
  query: Delta;
  setQuery: React.Dispatch<React.SetStateAction<Delta>>;
  hasFocus: boolean;
  isExpanded: boolean;
  blur: () => void;
} | null>(null);

export const useSearchBar = () => {
  const context = useContext(SearchBarContext);
  if (!context) {
    throw new Error('useSearchBarContext must be used within SearchBarContext');
  }

  return context;
};

interface SearchBarRootProps {
  children: React.ReactNode;
  onExpandedChange?: (expanded: boolean) => void;
  forceFocus?: boolean;
}

const Root = ({
  children,
  onExpandedChange,
  forceFocus,
}: SearchBarRootProps) => {
  const { quill } = useQuill();
  const location = useLocation();

  const setQuill = useSetAtom(searchBarQuillAtom);

  useEffect(() => {
    if (quill) {
      setQuill(quill);
    }
  }, [quill, setQuill]);

  const [searchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';

  const [query, setQuery] = useState(() => deserializeQuery(q));
  const [hasFocusState, setHasFocus] = useState(false);
  const hasFocus = forceFocus ?? hasFocusState;

  const isExpanded = query.length() > 1 || hasFocus;

  const onExpandedChangeRef = useRef(onExpandedChange);
  useLayoutEffect(() => {
    onExpandedChangeRef.current = onExpandedChange;
  }, [onExpandedChange]);

  useEffect(() => {
    if (onExpandedChangeRef.current) {
      onExpandedChangeRef.current(isExpanded);
    }
  }, [isExpanded]);

  useEffect(() => {
    if (!quill) {
      return;
    }

    const isSearchPage = location.pathname === '/search';
    const currentQuery = serializeQuery(quill.getContents());

    if (!isSearchPage && currentQuery) {
      quill.setContents(new Delta([{ insert: '\n' }]));
      return;
    }
    if (isSearchPage && q !== currentQuery) {
      quill.setContents(deserializeQuery(q));
    }
  }, [q, quill, location.pathname]);

  const blur = useCallback(() => {
    setHasFocus(false);

    if (quill?.getSelection(false)) {
      quill.blur();
    }
  }, [quill, setHasFocus]);

  useEffect(() => {
    if (!forceFocus) {
      blur();
    }
  }, [location, blur, forceFocus]);

  useEffect(() => {
    if (!quill) return undefined;

    setHasFocus(quill.hasFocus());

    function handleSelectionChange(
      currentRange: Range | null,
      oldRange: Range | null,
    ) {
      if (currentRange && !oldRange) {
        setHasFocus(true);
      } else if (!currentRange && oldRange) {
        blur();
      }
    }

    quill.on(Quill.events.SELECTION_CHANGE, handleSelectionChange);

    return () => {
      quill.off(Quill.events.SELECTION_CHANGE, handleSelectionChange);
    };
  }, [quill, blur, setHasFocus]);

  const contextValue = useMemo(
    () => ({
      query,
      setQuery,
      hasFocus,
      isExpanded,
      blur,
    }),
    [query, setQuery, hasFocus, isExpanded, blur],
  );

  useEffect(() => {
    if (forceFocus) {
      quill?.focus();
    }
  }, [forceFocus, quill]);

  return (
    <SearchBarContext.Provider value={contextValue}>
      {children}
    </SearchBarContext.Provider>
  );
};

interface SearchBarProps {
  quickJump?: boolean;
}

const Input = React.forwardRef<{ focus(): void }, SearchBarProps>(
  ({ quickJump = false }: SearchBarProps, inputRef) => {
    const { quill } = useQuill();
    const searchFormRef = useRef<HTMLFormElement>(null);

    useImperativeHandle(inputRef, () => ({
      focus() {
        quill?.focus();
      },
    }));

    const router = useRouter();

    const { query, setQuery, hasFocus, isExpanded, blur } = useSearchBar();

    const queryString = useMemo(() => serializeQuery(query), [query]);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const focusIndexChangedRef = useRef<boolean>(false);
    const { triggeredFacet, text, range } = usePrefixTriggeredFacet(quill);

    const handleChangeFocusedIndex = (index: number) => {
      focusIndexChangedRef.current = true;
      setFocusedIndex(index);
    };

    useEffect(() => {
      if (hasFocus) {
        focusIndexChangedRef.current = false;
        setFocusedIndex(0);
      }
    }, [query, hasFocus]);

    const dropdown: Dropdown | null = (() => {
      if (range && triggeredFacet) {
        if (triggeredFacet.type === 'entity') {
          return {
            type: 'entity',
            range,
            facet: triggeredFacet,
          };
        }
        if (triggeredFacet.type === 'date') {
          return {
            type: 'date',
            range,
            facet: triggeredFacet,
          };
        }
      }

      if (hasFocus && query.filter(isSearchFacetOp).length === 0) {
        return {
          type: 'suggestions',
        };
      }

      return null;
    })();

    const trimmedQuery = queryString.trim();

    const searchLocation = trimmedQuery
      ? { pathname: '/search', query: { q: trimmedQuery } }
      : undefined;
    const aiAskLocation = getAiAskLocation(searchLocation);

    const sections = useSearchSections({
      query,
      queryString,
      quickJump,
      searchLocation,
      aiAskLocation,
      dropdown,
    });

    const results = _.flatten<typeof sections[number]['items']>(
      sections.map((section) => section.items),
    );
    const hasSuggestedResults = results.length > 0;

    const handleSubmit = (event?: React.SyntheticEvent) => {
      blur();
      if (!searchLocation) {
        return;
      }
      event?.preventDefault();
      router.push(searchLocation);
    };

    return (
      <FacetEditProvider>
        <SearchObjectsProvider>
          <form
            ref={searchFormRef}
            onSubmit={handleSubmit}
            className={styles.container}
            role="search"
          >
            <TutorialBeacon id={BEACON_SEARCH_INPUT} placement="left">
              {(beaconProps) => (
                <div {...beaconProps}>
                  <FacetedSearchInput
                    expanded={isExpanded || queryString !== ''}
                    onChange={setQuery}
                    placeholder="Search or jump to…"
                    onEnter={() => {
                      const isEnterHandledByDropdown =
                        dropdown?.type === 'suggestions'
                          ? hasSuggestedResults
                          : dropdown != null;

                      if (!isEnterHandledByDropdown) {
                        handleSubmit();
                      }
                    }}
                  />
                </div>
              )}
            </TutorialBeacon>
          </form>
          {quill && dropdown && (
            <div className={styles.dropdownMenuContainer}>
              {dropdown.type === 'entity' && (
                <FacetDropdown
                  quill={quill}
                  range={dropdown.range}
                  facet={dropdown.facet}
                  text={text}
                  inputContainer={searchFormRef.current}
                  onClose={blur}
                />
              )}
              {dropdown.type === 'date' && (
                <SearchDatePicker
                  quill={quill}
                  range={dropdown.range}
                  facet={dropdown.facet}
                  onClose={blur}
                  searchFormRef={searchFormRef}
                  text={text}
                />
              )}
              {dropdown.type === 'suggestions' && hasSuggestedResults && (
                <DropdownMenu.Root
                  hotkeysEnabled
                  sections={sections}
                  index={focusedIndex}
                  onIndexChange={handleChangeFocusedIndex}
                  onSelect={(item) => {
                    if (item.data.type === 'searchOption' && item.data?.query) {
                      blur();
                      const nextQuery = item.data.query;
                      setQuery(nextQuery);
                      quill.setContents(nextQuery);

                      setTimeout(() => {
                        quill.setSelection({
                          index: quill.getLength(),
                          length: 0,
                        });
                      });
                    } else if (item.link) {
                      blur();
                    }
                  }}
                  onClose={blur}
                  maxHeight="75vh"
                  inputContainer={searchFormRef.current}
                  renderItem={(data, { focused }) => {
                    switch (data.type) {
                      case 'post':
                        return (
                          <PostSelectOption
                            {...data}
                            post={data.post}
                            focused={focused}
                            disablePreview={!focusIndexChangedRef.current}
                          />
                        );

                      case 'searchLink':
                        return <SearchLinkSelectOption {...data} />;
                      case 'topic':
                        return <TopicSelectOption {...data} />;
                      case 'user':
                        return <UserSelectOption {...data} />;
                      case 'group':
                        // Groups are not implemented as facets
                        return null;
                      default:
                        return <SelectEntityOption {...data} />;
                    }
                  }}
                />
              )}
            </div>
          )}
        </SearchObjectsProvider>
      </FacetEditProvider>
    );
  },
);

export default {
  Root: withQuillContextProvider(Root),
  Input,
};
